import { Component, Input, OnInit } from '@angular/core';
import { LogedUser } from '@base/src/app/core/models/logged-user.model';
import {
  ReferredDeduction,
  RenditionModelBE
} from '@base/src/app/models/renditions/RenditionModelBE.model';
import { CommissionSummary } from '@base/src/app/models/renditions/commission-summary.model';
import {
  BillingListPreview,
  DeductionKey,
  DeductionType,
  Deductions,
  ReferredsPreview
} from '@base/src/app/models/renditions/renditionPreview.model';
import { RenditionsService } from '@base/src/app/services/renditions/renditions.service';
import { ReservasListService } from '@base/src/app/services/renditions/reservas-list.service';
import { UtilsService } from '@base/src/app/services/utils.service';
import { Constants } from 'src/app/models/constants';
import { environment } from '@base/environments/environment';
import { AGENT_TYPE_PERCENTAGES } from '../agent-type-percentages';

@Component({
  selector: 'app-rendition-print-view',
  templateUrl: './rendition-print-view.component.html',
  styleUrls: ['./rendition-print-view.component.scss']
})
export class RenditionPrintViewComponent {
  @Input() data!: RenditionModelBE;
  @Input() dataLogin!: LogedUser | undefined;
  @Input() officesList!: { id: number; name: string }[];

  readonly CURRENCY_USD = 'U$S';
  readonly CURRENCY_LOCAL = '$';

  renditionExists = false;

  renditionID: number | undefined;

  date = ''; //FECHA DE LA RENDICION

  //QUÉ PUNTAS SON MIAS
  sideSellerOwn = false;
  sideBuyerOwn = false;

  myOffice = ''; //NOMBRE DE MI OFICINA
  myOfficeID = 0; //ID DE MI OFICINA

  //LISTA DE REFERIDOS
  referredList: ReferredsPreview = {
    seller: null,
    buyer: null
  };

  //LISTA DE FACTURAS
  billingsList: BillingListPreview[] = [];

  // DEDUCCIONES
  deductionList: Deductions = {
    agentSeller: null,
    agentBuyer: null,
    referredSeller: null,
    referredBuyer: null,
    teamLeaderSeller: null,
    teamLeaderBuyer: null,
    referred: null
  };
  deductionsKeys: DeductionKey[] = [];

  @Input() dataCheckout!: CommissionSummary;
  isReadyCheckout = false;

  isOnlyUsd = false; //Si la rendición es solo en dólares
  isOnlyLocal = false; //Si la rendición es solo en pesos

  dataAgentTypes: Record<string, number> = AGENT_TYPE_PERCENTAGES;

  title = '';

  cobrokerUSD = 0;
  cobrokerLocal = 0;

  readonly NODE = environment.node;
  readonly NODE_DO = Constants.TENANT_ID_DO;
  readonly NODE_AR = Constants.TENANT_ID_AR;
  readonly NODE_EC = Constants.TENANT_ID_EC;
  readonly NODE_PY = Constants.TENANT_ID_PY;
  readonly NODE_GLOBAL = Constants.TENANT_ID_GLOBAL;

  constructor(
    private readonly utils: UtilsService,
    public renditionsService: RenditionsService,
    public getReservasService: ReservasListService
  ) {
    this.dataCheckout = new CommissionSummary();
  }

  async ngOnChanges() {
    if (this.dataLogin) {
      this.myOffice = this.dataLogin.agency_name;
      this.myOfficeID = this.dataLogin.agency_id;
    }
    if (this.data.id != 0) {
      this.renditionExists = true;
      this.renditionID = this.data.id;
      this.title =
        this.getTypeNameRendition(this.data.renditionType) +
        ' #' +
        this.renditionID;
      this.sideSellerOwn =
        this.data.organizationCommission.purchaseOrganizationId != 0;
      this.sideBuyerOwn =
        this.data.organizationCommission.saleOrganizationId != 0;
      this.getDate();
      this.getReferredList();
      this.getBillingsList();
      this.getDeductionsList();
      this.deductionsKeys = [];
      this.deductionsKeys = Object.keys(this.deductionList) as DeductionKey[];
      this.getActivescurrencys();
      this.isReadyCheckout =
        this.dataCheckout.closedPrice.local != 0 ||
        this.dataCheckout.closedPrice.usd != 0;
      if (this.NODE === this.NODE_DO) {
        this.cobrokerUSD =
          this.dataCheckout.cobrokers?.reduce((sum, cobroker) => {
            return sum + cobroker.rawCommission.usd;
          }, 0) ?? 0;
        this.cobrokerLocal =
          this.dataCheckout.cobrokers?.reduce((sum, cobroker) => {
            return sum + cobroker.rawCommission.local;
          }, 0) ?? 0;
      }
    }
  }

  //--------------------------------
  //--------FUNCIONES UTILES--------
  //--------------------------------

  getDate() {
    this.date = this.utils.getFormattedDate(
      this.data?.renditionDate.toString()
    );
  }

  displayFormat(n: number | string | null | boolean | undefined): string {
    let r = '-';
    if (typeof n === 'number') {
      let num = n;
      if (num % 1 !== 0) {
        num = Number(num.toFixed(2));
      }
      r = num.toLocaleString('es-AR');
    } else if (typeof n === 'string' && n != '') {
      r = n;
    }

    return r;
  }

  get statisticsComputationLabel(): string | null {
    if (this.NODE !== this.NODE_DO) {
      return null;
    }
    if ([2, 5].some((n) => n == this.data?.renditionType)) {
      return null;
    }

    const hasOfficeTeamLeader = !!(
      this.dataCheckout?.seller?.teamLeader?.name ||
      this.dataCheckout?.buyer?.teamLeader?.name
    );
    const hasCobrokerTeamLeader = !!this.dataCheckout?.cobrokers?.some(
      (cobroker) => !!cobroker.teamLeader?.name,
    );
    if (!hasOfficeTeamLeader && !hasCobrokerTeamLeader) {
      return null;
    }

    const source =
      this.data?.teamLeaderDeductions?.find((e) => e.deductionType === 1) ??
      this.data?.teamLeaderDeductions?.find((e) => e.deductionType === 2) ??
      this.data?.cobrokerTeamLeaders?.find((e) => !!e.organizationName) ??
      this.data?.cobrokerTeamLeaders?.[0];

    return source?.checkTeamLeader === 0
      ? '100% Team Member'
      : 'Split Member/Leader';
  }

  getTypeNameRendition(type: number): string {
    const renditionsType = [
      {
        value: 1,
        text: 'Rendición'
      },
      {
        value: 2,
        text: 'Referido a cobrar'
      },
      {
        value: 3,
        text: 'Cuota'
      },
      {
        value: 4,
        text: 'Rendición borrador'
      },
      {
        value: 5,
        text: 'Referido borrador'
      },
      {
        value: 6,
        text: 'Cuota borrador'
      },
      {
        value: 7,
        text: 'Ingreso relacionado'
      },
      {
        value: 8,
        text: 'Ingreso relacionado borrador'
      }
    ];

    const name = renditionsType.find((e) => e.value == type)?.text ?? '-';

    return name;
  }

  getActivescurrencys() {
    this.isOnlyLocal =
      this.dataCheckout.seller.usd == 0 &&
      this.dataCheckout.buyer.usd == 0 &&
      this.dataCheckout.seller.referred.rawCommission.usd == 0 &&
      this.dataCheckout.buyer.referred.rawCommission.usd == 0;

    this.isOnlyUsd =
      this.dataCheckout.seller.local == 0 &&
      this.dataCheckout.buyer.local == 0 &&
      this.dataCheckout.seller.referred.rawCommission.local == 0 &&
      this.dataCheckout.buyer.referred.rawCommission.local == 0;
  }

  showSubtotalOffice() {
    const existsSellerNotOwn: boolean =
      this.dataCheckout.seller.officeId != null ||
      (this.dataCheckout.seller.otherRealEstateName != null &&
        this.dataCheckout.seller.otherRealEstateName != '');

    const existsBuyerNotOwn: boolean =
      this.dataCheckout.buyer.officeId != null ||
      (this.dataCheckout.buyer.otherRealEstateName != null &&
        this.dataCheckout.buyer.otherRealEstateName != '');

    const existsRefSellerNotOwn: boolean =
      this.referredList.seller != null &&
      this.referredList.seller.officeID != this.myOfficeID;

    const existsRefBuyerNotOwn: boolean =
      this.referredList.buyer != null &&
      this.referredList.buyer.officeID != this.myOfficeID;

    return (
      existsSellerNotOwn ||
      existsBuyerNotOwn ||
      existsRefSellerNotOwn ||
      existsRefBuyerNotOwn
    );
  }

  getTagExclusivityBuyer(): string {
    let tagBuyer = 'Búsqueda';
    const tagSeller = this.data.exclusivityTypeTxt;

    if (
      this.sideBuyerOwn &&
      this.sideSellerOwn &&
      this.data.organizationCommission.purchaseOrganizationId ==
        this.data.organizationCommission.saleOrganizationId
    ) {
      tagBuyer = tagSeller;
    }

    return tagBuyer;
  }

  getOfficeName(id: number | null) {
    let name = 'Otro';
    if (id) {
      const agency = this.officesList.find(
        (e: { id: number; name: string }) => e.id == id
      );
      name = agency?.name ?? '-';
    }
    return name;
  }

  getReferredList() {
    this.referredList = { seller: null, buyer: null };
    const buildData = (ref: ReferredDeduction) => {
      let office = 0;
      if (ref.agencyId == 0 && ref.organizationId != 0) {
        office = this.myOfficeID;
      } else if (ref.agencyId == 0 && ref.organizationId == 0) {
        office = -1;
      } else if (ref.agencyId) {
        office = ref.agencyId;
      }
      return {
        name: ref.organizationName,
        officeID: office
      };
    };

    if (
      this.data?.referredDeductions &&
      this.data.referredDeductions.length > 0
    ) {
      const seller = this.data.referredDeductions.find(
        (e) => e.typePayment == 1
      );
      if (seller) {
        this.referredList.seller = buildData(seller);
      }

      const buyer = this.data.referredDeductions.find(
        (e) => e.typePayment == 2
      );
      if (buyer) this.referredList.buyer = buildData(buyer);
    }
  }

  getBillingsList() {
    this.billingsList = [];
    if (this.data) {
      //RECORRO FACTURAS AGENTE A LA OFICINA
      this.data.agentPayments.forEach((e) => {
        const item: BillingListPreview = {
          origin: 'Agente a la oficina',
          type: e.voucherTypeTxt,
          agent: e.organizationName || '-',
          currency:
            e.currency == 'USD' ? this.CURRENCY_USD : this.CURRENCY_LOCAL,
          currencyIVA:
            e.ivaCurrency == 'USD' ? this.CURRENCY_USD : this.CURRENCY_LOCAL,
          amount: e.subtotalUSD || e.subtotalARS,
          iva: e.subtotalIVAUSD || e.subtotalIVAARS
        };
        this.billingsList.push(item);
      });
    }
  }

  getDeductionsList() {
    //inicializo
    this.deductionList = {
      agentSeller: null,
      agentBuyer: null,
      referredSeller: null,
      referredBuyer: null,
      teamLeaderSeller: null,
      teamLeaderBuyer: null,
      referred: null
    };

    if (this.dataCheckout.referred.isReferred) {
      // DEDUCCIONES AGENTE VENDEDOR
      if (this.sideSellerOwn) {
        const name =
          'Agente ' +
          (this.NODE === this.NODE_DO ? 'captador' : 'vendedor') +
          ': ' +
          this.data?.organizationCommission?.purchaseOrganizationName;

        const item: DeductionType = {
          name: name,
          jurisdictionDeduction: {
            percentage: this.data?.purchaseDeduction.deductionPercentage ?? 0,
            local: this.data?.purchaseDeduction.deductionAmountARS ?? 0,
            usd: this.data?.purchaseDeduction.deductionAmountUSD ?? 0,
            nameDeduction: ''
          },
          manualDeduction: null
        };

        const manualDec = this.data?.otherDeductions.find((e) => e.type == 1);

        if (manualDec) {
          item.manualDeduction = {
            percentage: manualDec?.percentage ?? 0,
            local: manualDec?.amountARS ?? 0,
            usd: manualDec?.amountUSD ?? 0,
            nameDeduction: manualDec.description
          };
        }

        this.deductionList.agentSeller = item;
      }
      // DEDUCCIONES AGENTE COMPRADOR
      if (this.sideBuyerOwn) {
        const name =
          'Agente ' +
          (this.NODE === this.NODE_DO ? 'vendedor' : 'comprador') +
          ': ' +
          this.data?.organizationCommission?.saleOrganizationName;

        const item: DeductionType = {
          name: name,
          jurisdictionDeduction: {
            percentage: this.data?.saleDeduction.deductionPercentage ?? 0,
            local: this.data?.saleDeduction.deductionAmountARS ?? 0,
            usd: this.data?.saleDeduction.deductionAmountUSD ?? 0,
            nameDeduction: ''
          },
          manualDeduction: null
        };

        const manualDec = this.data?.otherDeductions.find((e) => e.type == 2);

        if (manualDec) {
          item.manualDeduction = {
            percentage: manualDec?.percentage ?? 0,
            local: manualDec?.amountARS ?? 0,
            usd: manualDec?.amountUSD ?? 0,
            nameDeduction: manualDec.description
          };
        }

        this.deductionList.agentBuyer = item;
      }

      //DEDUCCIONES REFERIDO VENDEDOR
      if (
        this.referredList.seller &&
        this.referredList.seller.officeID == this.myOfficeID
      ) {
        const name =
          'Referido ' +
          (this.NODE === this.NODE_DO ? 'vendedor' : 'comprador') +
          ': ' +
          this.referredList.seller.name;

        const item: DeductionType = {
          name: name,
          jurisdictionDeduction: {
            percentage:
              this.dataCheckout.seller.referred.agentDeduction.percentage,
            local: this.dataCheckout.seller.referred.agentDeduction.local,
            usd: this.dataCheckout.seller.referred.agentDeduction.usd,
            nameDeduction: ''
          },
          manualDeduction: null
        };

        this.deductionList.referredSeller = item;
      }

      //DEDUCCIONES REFERIDO COMPRADOR
      if (
        this.referredList.buyer &&
        this.referredList.buyer.officeID == this.myOfficeID
      ) {
        const name =
          'Referido ' +
          (this.NODE === this.NODE_DO ? 'vendedor' : 'comprador') +
          ': ' +
          this.referredList.buyer.name;

        const item: DeductionType = {
          name: name,
          jurisdictionDeduction: {
            percentage:
              this.dataCheckout.buyer.referred.agentDeduction.percentage,
            local: this.dataCheckout.buyer.referred.agentDeduction.local,
            usd: this.dataCheckout.buyer.referred.agentDeduction.usd,
            nameDeduction: ''
          },
          manualDeduction: null
        };

        this.deductionList.referredSeller = item;
      }

      // DEDUCCIONES REFERIDO A COBRAR
    } else {
      const NAME =
        'Agente ' +
        (this.dataCheckout.referred.isSeller
          ? this.NODE === this.NODE_DO
            ? 'captador'
            : 'vendedor'
          : this.NODE === this.NODE_DO
            ? 'vendedor'
            : 'comprador') +
        ': ' +
        this.data?.organizationCommission?.saleOrganizationName;

      const ITEM: DeductionType = {
        name: NAME,
        jurisdictionDeduction: {
          percentage: this.dataCheckout.referred.deduction.percentage ?? 0,
          local: this.dataCheckout.referred.deduction.local ?? 0,
          usd: this.dataCheckout.referred.deduction.usd ?? 0,
          nameDeduction: ''
        },
        manualDeduction: null
      };

      const MANUAL_DEC =
        this.dataCheckout.referred.manualDeduction.local > 0 ||
        this.dataCheckout.referred.manualDeduction.usd > 0;

      if (MANUAL_DEC) {
        ITEM.manualDeduction = {
          percentage: 0,
          local: this.dataCheckout.referred.manualDeduction.local ?? 0,
          usd: this.dataCheckout.referred.manualDeduction.usd ?? 0,
          nameDeduction: this.dataCheckout.referred.manualDeduction.name
        };
      }

      this.deductionList.referred = ITEM;
    }
  }
}
